import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Shield, Briefcase, FileText, GraduationCap, Search, MessageSquare, ArrowRight, ChevronRight, Star, Users } from 'lucide-react';

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-navy-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Shield className="w-8 h-8 text-amber-600" />
            <span className="font-heading font-bold text-xl text-navy-900">Atlas Career Solutions</span>
          </Link>
          <div className="hidden md:flex items-center gap-6">
            <Link to="/about" className="text-navy-600 hover:text-amber-600 font-medium">
              About
            </Link>
            <Link to="/testimonials" className="text-navy-600 hover:text-amber-600 font-medium">
              Success Stories
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-navy-700 hover:text-amber-600" data-testid="nav-login-btn">
                Sign In
              </Button>
            </Link>
            <Link to="/register">
              <Button className="bg-navy-900 hover:bg-navy-800 text-white" data-testid="nav-register-btn">
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-navy-50 to-white"></div>
        <div className="absolute top-20 right-0 w-96 h-96 bg-amber-100 rounded-full blur-3xl opacity-40"></div>
        
        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-medium mb-6">
                <Shield className="w-4 h-4" />
                Trusted by 10,000+ Veterans
              </span>
              <h1 className="font-heading text-5xl md:text-6xl font-bold text-navy-900 tracking-tight leading-tight mb-6">
                Your Military Service, <span className="text-amber-600">Your Civilian Success</span>
              </h1>
              <p className="text-lg text-navy-600 leading-relaxed mb-8 max-w-xl">
                Atlas Career Solutions empowers Veterans and service members with AI-powered guidance for benefits navigation, career transition, and professional development.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/register">
                  <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-6 text-lg shadow-lg shadow-amber-200 hover:scale-[1.02] transition-all" data-testid="hero-get-started-btn">
                    Start Your Transition
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button size="lg" variant="outline" className="border-2 border-amber-500 text-amber-600 hover:bg-amber-50 px-8 py-6 text-lg" data-testid="hero-login-btn">
                    I Have an Account
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="relative animate-fade-in stagger-2">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1763739527730-a6a425d17e3a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHx2ZXRlcmFuJTIwaW4lMjBzdWl0JTIwb2ZmaWNlfGVufDB8fHx8MTc3MjA1NjMzOXww&ixlib=rb-4.1.0&q=85&w=800"
                  alt="Professional mentorship"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/60 to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <p className="text-white font-medium">From Service to Success</p>
                  <p className="text-white/80 text-sm">Your experience matters here</p>
                </div>
              </div>
              
              {/* Floating stats card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 border border-navy-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Briefcase className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-navy-900">94%</p>
                    <p className="text-sm text-navy-500">Career Placement Rate</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Bento Grid */}
      <section className="py-20 px-6 bg-navy-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 animate-fade-in">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-navy-900 mb-4">
              Everything You Need to Succeed
            </h2>
            <p className="text-lg text-navy-600 max-w-2xl mx-auto">
              Comprehensive tools and AI-powered guidance designed specifically for military transition
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Benefits Chat - Tall Card */}
            <Card className="md:row-span-2 group hover:shadow-xl transition-all duration-300 border-navy-100 hover:border-amber-200 overflow-hidden">
              <CardContent className="p-0 h-full flex flex-col">
                <div className="h-48 bg-gradient-to-br from-navy-900 to-navy-700 relative overflow-hidden">
                  <Shield className="absolute -right-8 -bottom-8 w-40 h-40 text-white/10" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <MessageSquare className="w-16 h-16 text-amber-400" />
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="font-heading text-2xl font-bold text-navy-900 mb-3">Veterans Benefits Advisor</h3>
                  <p className="text-navy-600 flex-1">
                    AI-powered guidance through VA healthcare, GI Bill, home loans, disability compensation, and more. Get personalized answers based on your service record.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Career Chat */}
            <Card className="group hover:shadow-xl transition-all duration-300 border-navy-100 hover:border-amber-200">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Briefcase className="w-7 h-7 text-amber-600" />
                </div>
                <h3 className="font-heading text-xl font-bold text-navy-900 mb-2">Career Advisor</h3>
                <p className="text-navy-600 text-sm">
                  Industry-leading career advice with MOS translation, interview prep, and salary negotiation strategies.
                </p>
              </CardContent>
            </Card>

            {/* Document Generator */}
            <Card className="group hover:shadow-xl transition-all duration-300 border-navy-100 hover:border-amber-200">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-navy-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <FileText className="w-7 h-7 text-navy-600" />
                </div>
                <h3 className="font-heading text-xl font-bold text-navy-900 mb-2">Document Generator</h3>
                <p className="text-navy-600 text-sm">
                  Create resumes, cover letters, reference letters, and business plans tailored for civilian employers.
                </p>
              </CardContent>
            </Card>

            {/* Job Search - Wide Card */}
            <Card className="lg:col-span-2 group hover:shadow-xl transition-all duration-300 border-navy-100 hover:border-amber-200">
              <CardContent className="p-6 flex items-center gap-6">
                <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Search className="w-10 h-10 text-white" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold text-navy-900 mb-2">Smart Job Search</h3>
                  <p className="text-navy-600">
                    Find veteran-friendly opportunities across the web. AI-powered matching based on your military skills and career preferences.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Learning Suite */}
            <Card className="group hover:shadow-xl transition-all duration-300 border-navy-100 hover:border-amber-200">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <GraduationCap className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="font-heading text-xl font-bold text-navy-900 mb-2">Learning Suite</h3>
                <p className="text-navy-600 text-sm">
                  Professional development courses with videos and downloadable resources for career success.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-16 px-6 border-y border-navy-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="font-heading text-4xl font-bold text-navy-900">10K+</p>
              <p className="text-navy-600">Veterans Served</p>
            </div>
            <div>
              <p className="font-heading text-4xl font-bold text-amber-600">94%</p>
              <p className="text-navy-600">Placement Rate</p>
            </div>
            <div>
              <p className="font-heading text-4xl font-bold text-navy-900">500+</p>
              <p className="text-navy-600">Partner Employers</p>
            </div>
            <div>
              <p className="font-heading text-4xl font-bold text-amber-600">4.9/5</p>
              <p className="text-navy-600">User Rating</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-navy-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-amber-500 rounded-full blur-[200px] opacity-20"></div>
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Start Your <span className="text-amber-400">Civilian Career?</span>
          </h2>
          <p className="text-xl text-navy-200 mb-10 max-w-2xl mx-auto">
            Join thousands of veterans who have successfully transitioned with Atlas Career Solutions.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="bg-amber-600 hover:bg-amber-500 text-white px-10 py-6 text-lg shadow-lg hover:scale-[1.02] transition-all" data-testid="cta-register-btn">
                Create Free Account
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-navy-950 text-navy-300">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-amber-500" />
              <span className="font-heading font-bold text-white">Atlas Career Solutions</span>
            </div>
            <p className="text-sm">
              &copy; {new Date().getFullYear()} Atlas Career Solutions. Empowering Veterans, One Career at a Time.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
